import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { RefreshReactQuery } from "../../constants/AppConstants";
import { ERROR_LABEL, ERROR_MESSAGE_LABEL } from "../../constants/Dictionary";
import CmSheetStateMachineService from "../../services/api/cmSheet/CmSheetStateMachine.service";
import { UpdateCmSheetStateMachinewRequest } from "../../types/api/collaborators.type";
import { useNotification } from "../notification.hook";

export const CM_SHEET_STATE_MACHINE_QUERY_KEY = "get-cm-sheet-state-machine";

export const useCmSheetStateMachine = (
  cmSheetId: number | undefined,
  enabled = true,
) => {
  const { notification } = useNotification();

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: [CM_SHEET_STATE_MACHINE_QUERY_KEY, cmSheetId],
    queryFn: () => CmSheetStateMachineService.get(cmSheetId!),
    staleTime: RefreshReactQuery.MODERATE,
    enabled: cmSheetId != undefined && enabled,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching, refetch, isError };
};

export const useUpdateCmSheetStateMachine = (cmSheetId: number) => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync: updateCmSheetStateMachine, isPending: isUpdating } =
    useMutation({
      mutationFn: (body: UpdateCmSheetStateMachinewRequest) =>
        CmSheetStateMachineService.update(cmSheetId, body),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [CM_SHEET_STATE_MACHINE_QUERY_KEY, cmSheetId],
        });
      },
      onError: (error) => {
        console.error(error);
        notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
      },
    });

  return { updateCmSheetStateMachine, isUpdating };
};
